'use client';

import { useState, useEffect } from 'react';
import { Clock, ChevronDown, ChevronUp, Info, Lock, Loader2 } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import type { Pick } from '@/lib/supabase';

function formatOdds(odds: number): string {
  return odds > 0 ? `+${odds}` : `${odds}`;
}

function formatGameTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York', timeZoneName: 'short' });
}

function getBetTypeBadge(type: string) {
  const map: Record<string, string> = {
    spread: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
    moneyline: 'bg-purple-500/10 text-purple-400 border-purple-500/20',
    total: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
    prop: 'bg-pink-500/10 text-pink-400 border-pink-500/20',
    parlay: 'bg-[hsl(var(--primary)/0.1)] text-[hsl(var(--primary))] border-[hsl(var(--primary)/0.2)]',
  };
  return map[type] || 'bg-gray-500/10 text-gray-400 border-gray-500/20';
}

function getOddsColor(odds: number): string {
  if (odds > 0) return 'text-[hsl(var(--primary))]';
  if (odds <= -150) return 'text-amber-400';
  return 'text-[hsl(var(--foreground))]';
}

function impliedProbability(odds: number): number {
  if (odds > 0) {
    return (100 / (odds + 100)) * 100;
  } else {
    const absOdds = Math.abs(odds);
    return (absOdds / (absOdds + 100)) * 100;
  }
}

function getProbabilityColor(prob: number): string {
  if (prob >= 65) return 'text-emerald-400';
  if (prob >= 50) return 'text-[hsl(var(--primary))]';
  if (prob >= 40) return 'text-amber-400';
  return 'text-rose-400';
}

const BET_TYPE_LABELS: Record<string, string> = {
  spread: 'Spread',
  moneyline: 'Moneyline',
  total: 'Total',
  prop: 'Prop',
  parlay: 'Parlay',
};

export default function TodaysPicks() {
  const [todayPicks, setTodayPicks] = useState<Pick[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => {
    async function loadPicks() {
      try {
        const { data, error } = await supabase
          .from('picks')
          .select('*')
          .eq('result', 'pending')
          .order('game_time', { ascending: true });

        if (error) throw error;
        setTodayPicks(data || []);
      } catch {
        setTodayPicks([]);
      } finally {
        setLoading(false);
      }
    }
    loadPicks();
  }, []);

  const bySport = todayPicks.reduce<Record<string, Pick[]>>((acc, pick) => {
    if (!acc[pick.sport]) acc[pick.sport] = [];
    acc[pick.sport].push(pick);
    return acc;
  }, {});

  const sportIcons: Record<string, string> = {
    NBA: '🏀', NFL: '🏈', NHL: '🏒', MLB: '⚾', NCAAB: '🏀', Soccer: '⚽', UFC: '🥊',
  };

  const today = new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });

  if (loading) {
    return (
      <div className="mb-10">
        <h2 className="text-xl font-bold text-[hsl(var(--foreground))] mb-4 flex items-center gap-2">
          <Clock size={20} className="text-[hsl(var(--primary))]" />
          Today&apos;s Picks
        </h2>
        <div className="stat-card flex items-center justify-center py-16 gap-2">
          <Loader2 size={20} className="animate-spin text-[hsl(var(--primary))]" />
          <span className="text-sm text-[hsl(var(--foreground-muted))]">Loading picks...</span>
        </div>
      </div>
    );
  }

  if (todayPicks.length === 0) {
    return (
      <div className="mb-10">
        <h2 className="text-xl font-bold text-[hsl(var(--foreground))] mb-4 flex items-center gap-2">
          <Clock size={20} className="text-[hsl(var(--primary))]" />
          Today&apos;s Picks
          <span className="font-mono text-xs text-[hsl(var(--foreground-muted))] font-normal ml-2">{today}</span>
        </h2>
        <div className="stat-card flex flex-col items-center justify-center py-16 gap-4">
          <Lock size={40} className="text-[hsl(var(--foreground-muted))]" />
          <div className="text-center">
            <p className="font-semibold text-lg text-[hsl(var(--foreground))]">No picks posted yet</p>
            <p className="text-sm text-[hsl(var(--foreground-muted))] mt-1">Picks are posted by 6:00 AM EST daily. Check back soon.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-10">
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-xl font-bold text-[hsl(var(--foreground))] flex items-center gap-2">
          <Clock size={20} className="text-[hsl(var(--primary))]" />
          Today&apos;s Picks
          <span className="font-mono text-xs text-[hsl(var(--foreground-muted))] font-normal ml-2">{today}</span>
        </h2>
        <div className="flex items-center gap-2">
          <span className="font-mono text-xs text-[hsl(var(--foreground-muted))]">
            {todayPicks.length} pick{todayPicks.length !== 1 ? 's' : ''} posted
          </span>
          <div className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--primary))] pulse-dot" />
          <span className="text-xs text-[hsl(var(--primary))] font-medium">LIVE</span>
        </div>
      </div>

      <div className="space-y-6">
        {Object.entries(bySport).map(([sport, picks]) => (
          <div key={sport}>
            <div className="flex items-center gap-2 mb-3">
              <span className="text-xl">{sportIcons[sport] || '🎯'}</span>
              <h3 className="text-base font-semibold text-[hsl(var(--foreground))]">{sport}</h3>
              <div className="h-px flex-1 bg-[hsl(var(--border))]" />
              <span className="text-xs text-[hsl(var(--foreground-muted))] font-mono">
                {picks.length} pick{picks.length !== 1 ? 's' : ''}
              </span>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-2 2xl:grid-cols-3 gap-4">
              {picks.map((pick) => {
                const isExpanded = expandedId === pick.id;
                const totalUnitsToWin = pick.odds > 0
                  ? (pick.units * pick.odds) / 100
                  : pick.units / (Math.abs(pick.odds) / 100);

                return (
                  <div key={pick.id} className="pick-card">
                    <div className="px-5 pt-4 pb-3 border-b border-[hsl(var(--border))]">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-[hsl(var(--foreground))] text-sm truncate">{pick.game}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <Clock size={11} className="text-[hsl(var(--foreground-muted))]" />
                            <span className="text-xs text-[hsl(var(--foreground-muted))] font-mono">{formatGameTime(pick.game_time)}</span>
                          </div>
                        </div>
                        <span className={`odds-badge border ${getBetTypeBadge(pick.bet_type)} text-xs`}>
                          {BET_TYPE_LABELS[pick.bet_type]}
                        </span>
                      </div>
                    </div>

                    <div className="px-5 py-4">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex flex-col gap-0.5">
                          <span className="text-xs uppercase tracking-wider text-[hsl(var(--foreground-muted))] font-medium">Pick</span>
                          <span className="text-lg font-bold text-[hsl(var(--foreground))]">{pick.pick}</span>
                        </div>
                        <div className="flex flex-col items-end gap-0.5">
                          <span className="text-xs uppercase tracking-wider text-[hsl(var(--foreground-muted))] font-medium">Odds</span>
                          <span className={`font-mono text-2xl font-bold tabular-nums ${getOddsColor(pick.odds)}`}>{formatOdds(pick.odds)}</span>
                        </div>
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="flex flex-col gap-0.5">
                            <span className="text-xs text-[hsl(var(--foreground-muted))]">Units</span>
                            <span className="font-mono font-semibold text-sm text-[hsl(var(--foreground))]">{pick.units}u</span>
                          </div>
                          <div className="flex flex-col gap-0.5">
                            <span className="text-xs text-[hsl(var(--foreground-muted))]">To Win</span>
                            <span className="font-mono font-semibold text-sm text-[hsl(var(--primary))]">+{totalUnitsToWin.toFixed(2)}u</span>
                          </div>
                        </div>
                        <button
                          onClick={() => setExpandedId(isExpanded ? null : pick.id)}
                          className="flex items-center gap-1 text-xs text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))] transition-colors duration-150"
                        >
                          <Info size={13} />
                          Analysis
                          {isExpanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                        </button>
                      </div>

                      {isExpanded && pick.notes && (
                        <div className="mt-3 pt-3 border-t border-[hsl(var(--border))] fade-in">
                          <p className="text-xs text-[hsl(var(--foreground-muted))] leading-relaxed">📊 {pick.notes}</p>
                        </div>
                      )}
                    </div>

                    <div className="px-5 py-2.5 bg-[hsl(var(--card-elevated))] flex items-center justify-between">
                      <span className="result-pending text-xs px-2 py-0.5 rounded-full font-medium">⏳ Pending</span>
                      <span className="text-xs text-[hsl(var(--foreground-muted))] font-mono">
                        Posted {new Date(pick.posted_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York', timeZoneName: 'short' })}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}